/* eslint-disable react/jsx-props-no-spreading */
import { TextField, Autocomplete, CircularProgress } from "@mui/material"
import match from "autosuggest-highlight/match"
import parse from "autosuggest-highlight/parse"
import { ReactElement, SyntheticEvent } from "react"

import { ITEM_BORDER_MULTIPLIER } from "../../../themes/defaultThemeParams"
import { TFilterSelect } from "../../../webpages/lowCarbonTechDatabases/genDeviceSearch"

type TAppControlledSelectPropsKwarg = {
    value: Record<string, string | number> | null
    setValue: (e_: SyntheticEvent<Element, Event>, nv: TFilterSelect) => void
    options: Record<string, string | number>[]
    label: string
    loading: boolean
    optionName: string
}

/**
 * Controlled, reusable single select input. Options are supplied as array
 * of objects, where each object has an id and name. The ID field can take any
 * name. The name field is supplied to this component via the optionName prop.
 * This autocomplete uses option text highlight matching during search.
 *
 * @example
 * <AppControlledSelectKwarg
 *     value={value}
 *     setValue={setValue}
 *     options={[]}
 *     label"t"
 *     optionName="name"
 *     loading
 * />
 */
const AppControlledSelectKwarg = ({
    value,
    setValue,
    options,
    label,
    loading,
    optionName,
}: TAppControlledSelectPropsKwarg): ReactElement => (
    <Autocomplete
        value={value}
        onChange={setValue}
        disablePortal
        options={options}
        loading={loading}
        getOptionLabel={(option: Record<string, string | number>): string =>
            String(option[optionName])
        }
        renderOption={(props, option, { inputValue }) => {
            const matches = match(String(option[optionName]), inputValue, {
                insideWords: true,
            })
            const parts = parse(String(option[optionName]), matches)

            return (
                <li {...props}>
                    <div>
                        {parts.map((part, index) => (
                            <span
                                // eslint-disable-next-line react/no-array-index-key
                                key={part.text + part.highlight + index}
                                style={{
                                    fontWeight: part.highlight ? 700 : 400,
                                }}
                            >
                                {part.text}
                            </span>
                        ))}
                    </div>
                </li>
            )
        }}
        renderInput={(params) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { InputLabelProps, InputProps, ...otherParams } = params
            return (
                <TextField
                    {...otherParams}
                    label={label}
                    placeholder="Select option..."
                    InputProps={{
                        ...InputProps,
                        sx: { borderRadius: ITEM_BORDER_MULTIPLIER },
                        endAdornment: (
                            <>
                                {loading ? (
                                    <CircularProgress
                                        color="inherit"
                                        size={20}
                                    />
                                ) : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )
        }}
    />
)
export default AppControlledSelectKwarg
